import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { ReviewGradeType } from '@core/services/study-session.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import {
  TrainAsset,
  TrainCardSubType,
  TrainCardType,
  TrainFlashcard,
} from '../../deck-train.models';
import { TextTrainFront } from '../train-fronts/text-train-front/text-train-front';
import { AudioRecordTrainFront } from '../train-fronts/audio-record-train-front/audio-record-train-front';
import { AudioConversationTrainFront } from '../train-fronts/audio-conversation-train-front/audio-conversation-train-front';
import { VideoOnlyTrainFront } from '../train-fronts/video-only-train-front/video-only-train-front';
import { ShortTalkTrainFront } from '../train-fronts/short-talk-train-front/short-talk-train-front';
import type { ShortTalkConversationMessage } from '../train-fronts/short-talk-train-front/short-talk-train-front';
import { PronunciationTrainFront } from '../train-fronts/pronunciation-train-front/pronunciation-train-front';
import { TextSimpleTrainBack } from '../train-backs/text-simple-train-back/text-simple-train-back';
import { TextTypedOrVoiceTrainBack } from '../train-backs/text-typed-or-voice-train-back/text-typed-or-voice-train-back';
import { TextMultipleChoiceTrainBack } from '../train-backs/text-multiple-choice-train-back/text-multiple-choice-train-back';
import { TrainDailyGoalProgress } from '../train-daily-goal-progress/train-daily-goal-progress';
import {
  TrainReviewFeedback,
  TrainReviewFeedbackLabels,
} from '../train-review-feedback/train-review-feedback';
import { ThemeToggle } from '@app/components/theme-toggle/theme-toggle';

@Component({
  selector: 'app-train-card-shell',
  imports: [
    TextTrainFront,
    AudioRecordTrainFront,
    AudioConversationTrainFront,
    VideoOnlyTrainFront,
    ShortTalkTrainFront,
    PronunciationTrainFront,
    TextSimpleTrainBack,
    TextTypedOrVoiceTrainBack,
    TextMultipleChoiceTrainBack,
    TrainDailyGoalProgress,
    TrainReviewFeedback,
    ThemeToggle,
  ],
  templateUrl: './train-card-shell.html',
  styleUrl: './train-card-shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainCardShell {
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');

  readonly deckTitle = input.required<string>();
  readonly card = input.required<TrainFlashcard>();
  readonly isPracticeMode = input(false);
  readonly shortTalkSessionId = input<number | null>(null);
  readonly position = input.required<number>();
  readonly isBackVisible = input.required<boolean>();
  readonly isTypedOrVoiceCorrect = input<boolean | null>(null);
  readonly isTypedOrVoiceSelfAssessment = input(false);
  readonly typedOrVoiceUserAnswer = input.required<string>();
  readonly isMultipleChoiceCorrect = input<boolean | null>(null);
  readonly multipleChoiceSelectedOptionId = input<number | null>(null);
  readonly multipleChoiceSelectedOptionIds = input<number[]>([]);
  readonly multipleChoicePlayableOptionIds = input<number[]>([]);
  readonly isBusy = input(false);
  readonly imageAsset = input<TrainAsset | null>(null);
  readonly audioAsset = input<TrainAsset | null>(null);
  readonly videoAsset = input<TrainAsset | null>(null);
  readonly canSpeakQuestion = input(false);
  readonly questionAudioUrl = input('');
  readonly playingAudioUrl = input('');
  readonly showTrainingSessionProgress = input(false);
  readonly trainingSessionReviewedCards = input(0);
  readonly trainingSessionCardsTarget = input(0);
  readonly trainingSessionProgressLabel = input('');
  readonly selectedReviewGrade = input(ReviewGradeType.None);
  readonly shortTalkOpeningReplyRequestId = input(0);

  readonly continue = output<void>();
  readonly close = output<void>();
  readonly speak = output<void>();
  readonly shortTalkIntroStarted = output<void>();
  readonly playAudio = output<void>();
  readonly stopPlayback = output<void>();
  readonly userAnswerChange = output<string>();
  readonly selectedOptionIdChange = output<number>();
  readonly selectedOptionIdsChange = output<number[]>();
  readonly optionSpeak = output<number>();
  readonly reviewGradeChange = output<ReviewGradeType>();
  readonly shortTalkMessagesChange = output<ShortTalkConversationMessage[]>();
  readonly pronunciationAssessmentComplete = output<void>();
  private readonly shortTalkFront = viewChild(ShortTalkTrainFront);

  protected readonly TrainCardType = TrainCardType;
  protected readonly TrainCardSubType = TrainCardSubType;
  protected readonly ReviewGradeType = ReviewGradeType;
  readonly t = this.deckTranslations.t;

  get isQuestionAudioPlaying(): boolean {
    const questionAudioUrl = this.questionAudioUrl();
    return !!questionAudioUrl && this.playingAudioUrl() === questionAudioUrl;
  }

  get isFrontAudioPlaying(): boolean {
    const audioUrl = this.audioAsset()?.url;
    return !!audioUrl && this.playingAudioUrl() === audioUrl;
  }

  get feedbackLabels(): TrainReviewFeedbackLabels {
    return {
      title: this.t()['train.feedbackTitle'],
      again: this.t()['train.feedbackAgain'],
      againDescription: this.t()['train.feedbackAgainDescription'],
      hard: this.t()['train.feedbackHard'],
      hardDescription: this.t()['train.feedbackHardDescription'],
      good: this.t()['train.feedbackGood'],
      goodDescription: this.t()['train.feedbackGoodDescription'],
      easy: this.t()['train.feedbackEasy'],
      easyDescription: this.t()['train.feedbackEasyDescription'],
    };
  }

  get titleClass(): string {
    return `${this.frontLayoutClass()}-title`;
  }

  get cardClass(): string {
    return `${this.frontLayoutClass()}-card ${this.backLayoutClass()}-card`;
  }

  get closeClass(): string {
    return `${this.frontLayoutClass()}-close`;
  }

  get continueClass(): string {
    if (this.card().cardType === TrainCardType.ShortTalk) {
      return 'short-talk-train-continue';
    }

    if (this.card().cardSubType === TrainCardSubType.TextTypedOrVoice) {
      return 'text-typed-train-continue';
    }

    if (this.isChoiceCard()) {
      return 'text-multiple-choice-train-continue';
    }

    if (this.card().cardType === TrainCardType.AudioRecord) {
      return 'audio-record-train-continue';
    }

    if (this.card().cardType === TrainCardType.AudioConversation) {
      return 'audio-conversation-train-continue';
    }

    if (this.card().cardType === TrainCardType.Pronunciation) {
      return 'pronunciation-train-continue';
    }

    return 'text-simple-train-continue';
  }

  get continueLabel(): string {
    if (this.card().cardType === TrainCardType.ShortTalk) {
      return this.t()['train.continue'];
    }

    if (this.card().enableAiEvaluation && this.isBackVisible()) {
      return this.t()['train.continue'];
    }

    if (this.shouldShowIncorrectContinue()) {
      return this.t()['train.continue'];
    }

    return this.isBackVisible()
      ? this.t()['train.next']
      : this.t()['train.reveal'];
  }

  get backIsCorrect(): boolean | null {
    if (this.card().cardType === TrainCardType.ShortTalk) {
      return null;
    }

    if (this.card().cardSubType === TrainCardSubType.TextTypedOrVoice) {
      if (this.isTypedOrVoiceSelfAssessment()) {
        return null;
      }

      return this.isTypedOrVoiceCorrect();
    }

    if (this.isChoiceCard()) {
      return this.isMultipleChoiceCorrect();
    }

    return null;
  }

  protected shouldShowFeedback(): boolean {
    if (this.card().enableAiEvaluation) {
      return false;
    }
    if (
      this.card().cardType === TrainCardType.ShortTalk ||
      !this.isBackVisible()
    ) {
      return false;
    }

    if (this.card().cardType === TrainCardType.Pronunciation) {
      return true;
    }

    if (this.card().cardSubType === TrainCardSubType.TextSimple) {
      return true;
    }

    if (
      this.card().cardSubType === TrainCardSubType.TextTypedOrVoice &&
      this.isTypedOrVoiceSelfAssessment()
    ) {
      return true;
    }

    return this.backIsCorrect === true;
  }

  protected shouldShowContinue(): boolean {
    // AI-evaluated typed answers are submitted before the back of the card is
    // shown. Their correctness is intentionally unknown at that point, so do
    // not hide the control that lets the learner advance after seeing it.
    if (this.card().enableAiEvaluation && this.isBackVisible()) {
      return true;
    }

    return (
      this.card().cardType === TrainCardType.ShortTalk ||
      !this.isBackVisible() ||
      this.shouldShowIncorrectContinue()
    );
  }

  protected hiddenFeedbackGrades(): readonly ReviewGradeType[] {
    if (this.card().cardSubType === TrainCardSubType.TextSimple) {
      return [];
    }

    if (
      this.card().cardSubType === TrainCardSubType.TextTypedOrVoice &&
      this.isTypedOrVoiceSelfAssessment()
    ) {
      return [];
    }

    return [ReviewGradeType.Again];
  }

  protected shouldCenterFeedbackGrades(): boolean {
    return this.hiddenFeedbackGrades().length === 1;
  }

  protected isChoiceCard(): boolean {
    return (
      this.card().cardSubType === TrainCardSubType.TextSingleChoice ||
      this.card().cardSubType === TrainCardSubType.TextMultipleChoice
    );
  }

  protected onContinue(): void {
    const shortTalkFront = this.shortTalkFront();
    if (
      this.card().cardType === TrainCardType.ShortTalk &&
      shortTalkFront?.isIntroVisible()
    ) {
      shortTalkFront.startConversation();
      return;
    }

    this.continue.emit();
  }

  private shouldShowIncorrectContinue(): boolean {
    if (
      !this.isBackVisible() ||
      this.card().cardType === TrainCardType.ShortTalk ||
      this.card().cardType === TrainCardType.Pronunciation
    ) {
      return false;
    }

    if (this.card().cardSubType === TrainCardSubType.TextSimple) {
      return false;
    }

    return this.backIsCorrect === false;
  }

  private frontLayoutClass(): string {
    if (this.card().cardType === TrainCardType.AudioRecord) {
      return 'audio-record-train';
    }

    if (this.card().cardType === TrainCardType.AudioConversation) {
      return 'audio-conversation-train';
    }

    if (this.card().cardType === TrainCardType.VideoOnly) {
      return 'video-only-train';
    }

    if (this.card().cardType === TrainCardType.ShortTalk) {
      return 'short-talk-train';
    }

    if (this.card().cardType === TrainCardType.Pronunciation) {
      return 'pronunciation-train';
    }

    return 'text-simple-train';
  }

  private backLayoutClass(): string {
    if (this.card().cardType === TrainCardType.ShortTalk) {
      return 'short-talk-train';
    }

    if (this.card().cardType === TrainCardType.Pronunciation) {
      return 'pronunciation-train';
    }

    if (this.card().cardSubType === TrainCardSubType.TextTypedOrVoice) {
      return 'text-typed-train';
    }

    if (this.isChoiceCard()) {
      return 'text-multiple-choice-train';
    }

    return 'text-simple-train';
  }
}
